import type {PathTerrainFeature} from "../../../../Terrain/Heights/IsolineTypes.js";
import type {PathMaterialFeature} from "../../../../Terrain/Materials/@MaterialFeature.js";
import type {PathSceneryFeature} from "../../../../Terrain/Scenery/@SceneryFeature.js";
import {Vec2, Vec2Simple} from "../../../../Utils/ThreeJS/VectorStructs.js";
import {MapObject} from "../@MapObject.js";
import {GuideShape} from "./ObjMesh.js";
import {PathFill} from "./ObjPath/PathFill.js";
export {PathFill} from "./ObjPath/PathFill.js";

export enum UVType {
	flatOnWorld = "flatOnWorld",
	alongPath = "alongPath",
}

/*export enum JoinType {
	miter = "miter",
	bevel = "bevel",
	round = "round",
}*/

export class ObjPath {
	static GetSelected(obj: MapObject): ObjPath|n {
		//return obj.paths?.find(a=>a.selected) ?? obj.paths?.[0];
		return obj.paths?.find(a=>a.selected);
	}
	/** Generate a fresh stable path_id (used on path creation and on user "regen" click). */
	static NewPathID(): string {
		return `path_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
	}
	constructor(data: Partial<ObjPath>) { Object.assign(this, data); }

	selected = true;

	/** When true, the path is not rendered in the map-ui. UI: a "Render" checkbox — checking it deletes this field; unchecking sets it to true. */
	renderDisabled?: boolean|n;

	/** Stable path-level identifier used for deterministic RNG seeding (e.g. fBm shape noise).
	 *  Auto-generated on path creation. Empty string = legacy path; user can manually regen it. */
	path_id?: string|n;

	// styling
	// ==========

	color: number[]|n;
	opacity?: number|n; // opacity of the mesh (generated for the path), 0-1
	texture_id: string|n;
	texture_size: Vec2|n;

	// others
	// ==========

	guide_shape: GuideShape|n;
	guide_size: number|n;
	guide_alignPoints: boolean|n;

	// geometry
	// ==========

	zOffset: number|n;
	//uvType: UVType|n;
	width: number|n;
	//joinType: JoinType|n;

	/** Max turn angle (degrees) per outer-arc segment at a path joint: lower = smoother but more
	 *  expensive arcs; very high (eg. 9000) = every joint degrades to a 1-segment bevel (sharp miter
	 *  inside, single chord outside). null/undefined = legacy 8 segments; new paths default to 15. */
	maxTurnPerArcSegment? = 15;

	/** Whether this path loops back on itself (closed) or is open. The single source of truth
	 *  for path topology. Stray per-config `closed` JSON keys (removed 2026-07-28) are tolerated. */
	curve_closed?: boolean|n = false;

	inheritPoints?: InheritPointsConfig|n;
	points: Vec2Simple[] = [];

	/** Ordered list of transforms applied to this path's points (whether base or inherited). */
	transforms: PathTransform[] = [];

	// path "fill" tab/feature
	// ==========

	fill: PathFill|n;

	// Terrain feature fields (V3)
	// ==========

	/** V3: when present and `role != "none"`, contributes a terrain feature to the Poisson/AMR
	 *  solve (replaces the V2 `iso_enabled`+`iso_constraints` pair, removed Phase 7). See
	 *  `PathTerrainFeature` for the vocabulary; stray V2 JSON keys are tolerated, clean up manually. */
	terrainFeature?: PathTerrainFeature|n;

	// Set12 — path material feature (material assignment)
	// ==========

	/** Set12: when present, this path paints a material region (fill / stroke / fillAndStroke)
	 *  into the terrain splat field. Sibling of `terrainFeature`; consumed by
	 *  `collectMaterialRegions` (Terrain/Materials/Splat/MaterialRegions.ts). Same whitelist rule
	 *  as `terrainFeature`: MUST be forwarded in `PackageUpMapObjForTerrain` or it is silently
	 *  dropped (Set1 B3/B4 bug class). See `PathMaterialFeature`. */
	materialFeature?: PathMaterialFeature|n;

	// Set13 — path scenery feature (procedural scenery placement)
	// ==========

	/** Set13: when present, this path places SCENERY (trees/rocks — discrete objects) into a
	 *  fill / stroke / fillAndStroke region. Sibling of `terrainFeature` / `materialFeature`;
	 *  consumed by `collectSceneryRegions` (Terrain/Scenery/Placement/SceneryRegions.ts). ORTHOGONAL
	 *  to the material feature by design (§4.10). Same whitelist rule as the others: MUST be
	 *  forwarded in `PackageUpMapObjForTerrain` or it is silently dropped (Set1 B3/B4 bug class).
	 *  See `PathSceneryFeature`. */
	sceneryFeature?: PathSceneryFeature|n;
}

/** Per-side offset direction for the extrude transform.
 *  Closed source path → "in"/"out" (inward / outward of the ring).
 *  Open source path → "left"/"right" (relative to the path's travel direction). */
export type ExtrudeSide = "in" | "out" | "left" | "right";

/** How the extrude transform's offset copies are turned into meshes: `eachCopySeparate` = each offset copy becomes its own mesh.
 * `origToEachCopy` = a mesh between the orig points and each copy;
 * `copyToCopy` = exactly two copies, one polygon iterating copy1[0..N-1] then copy2[N-1..0] (winding normalised face-up for three.js). */
export type ExtrudeMeshGen = "eachCopySeparate" | "origToEachCopy" | "copyToCopy";

/** A single point-deriving transform. Exactly one of `extrude` or `interpolate` is set per
 *  entry (mutually exclusive; compose via multiple transform entries in order). Applied in
 *  array order by `ApplyPathTransformsToPoints`. */
export class PathTransform {
	constructor(data?: Partial<PathTransform>|n) { Object.assign(this, data); }

	/** When true, this transform entry is inert: it keeps its slot in the chain (so transform
	 *  indexes are preserved) but passes the previous layer's points through unchanged.
	 *  UI: an "Enabled" checkbox — checking it deletes this field; unchecking sets it to true. */
	disabled?: boolean|n;

	interpolate?: InterpolateConfig|n;

	extrude?: {
		/** Offset distance in metres. */
		distance: number;
		/** One entry per offset copy. Length determines how many copies are produced.
		 *  Closed source: each entry must be "in" or "out". Open source: each entry must be "left" or "right". */
		sides: ExtrudeSide[];
		/** How the offset copies are turned into meshes. See `ExtrudeMeshGen`. */
		meshGen?: ExtrudeMeshGen | n;
		/** Post-pass: collapse self-intersections in the offset copy (miter overshoot on tight corners, thin peninsulas): drops
		 *  loop-enclosed vertices, re-bridges the crossing, removes dups (never adds). Guarantees: no more points than orig,
		 *  no self-intersection. NOT guaranteed: ≥distance everywhere or ≤distance drift (reflex corners). "in"/"right" only; off by default. */
		collapseSelfIntersections?: boolean | n;
	} | n;
}

/** Describes how a path inherits its points from another path's points.
 *  When present (and resolvable), the system uses this instead of the literal `points` array. */
export class InheritPointsConfig {
	constructor(data?: Partial<InheritPointsConfig>|n) { Object.assign(this, data); }
	/** The `path_id` of the source path, ie. which one we copy points from. */
	source_pathID: string = "";
	useSourcePathIDForSeeding = true;
	/** When set (> 0), applies the first N transforms from the SOURCE path's pipeline to its base points
	 *  before inheriting (lets a target reuse e.g. its curve interpolation without the full stack).
	 *  null/0 = inherit the source's base unchanged (default). */
	applySourceTransformCount?: number|n;
}

/** `PathTransform.interpolate` config — Catmull-Rom tessellation of the point set + optional fBm-noise lateral displacement (`fbmNoise`).
 *  Home of former `ObjPath.curveType`/curve_*_config; CR params flattened from `catmullRom` sub-config; nested legacy JSON not auto-migrated.
 *  Field precedence: `metersPerOutputSegment` >0 → arc-length re-sampling; else `outputSegmentRatio` ≥1 → fixed points/segment; else 10. */
export class InterpolateConfig {
	constructor(data?: Partial<InterpolateConfig>|n) { Object.assign(this, data); }

	/** Catmull-Rom subtype. Mirrors the former `Curve_CatmullRom_Config.subtype`. */
	subtype?: Curve_CatmullRom_Subtype|n = "centripetal";
	/** Catmull-Rom tension (only meaningful when `subtype == "catmullrom"`). */
	tension?: number|n = 0.5;
	/** How many points to generate per control-point segment. Mutually exclusive with
	 *  `metersPerOutputSegment`; when both are set `metersPerOutputSegment` wins. See class doc. */
	outputSegmentRatio?: number|n = 5;
	/** When set (> 0), re-samples the Catmull-Rom backbone at uniform arc-length intervals of this many
	 *  metres — constant world-distance spacing keeps the fBm-noise normal/offset baseline uniform
	 *  (prevents the V-at-CP artefact of fixed points-per-segment). Takes precedence over
	 *  `outputSegmentRatio` when both are set. Unset → fall back to `outputSegmentRatio`. */
	metersPerOutputSegment?: number|n;

	/** Optional fBm-noise lateral-displacement layer. Absent/`disabled` = plain Catmull-Rom tessellation;
	 *  present & not disabled = each point offset perpendicular to its tangent by `env × roughnessAmp × noiseVal`
	 *  (multi-octave fBm in `noiseSpace`, envelope zeroes the offset at control points). */
	fbmNoise?: FbmNoiseLayer_Config|n;
}

export const Curve_CatmullRom_Subtype_values = ["centripetal", "chordal", "catmullrom"] as const;
export type Curve_CatmullRom_Subtype = typeof Curve_CatmullRom_Subtype_values[number];

/** The fBm-noise displacement layer on `InterpolateConfig`. Present-and-not-disabled = ON; `disabled: true` = OFF (inert, mirrors `PathTransform.disabled`).
 *  Knobs live here; backbone Catmull-Rom params one level up. Empty/null fields resolve at runtime: roughness/wavelength from RNG keyed on
 *  [worldSeed?, pathId, propName] (worldSeed only for isolines). */
export class FbmNoiseLayer_Config {
	constructor(data?: Partial<FbmNoiseLayer_Config>|n) { Object.assign(this, data); }

	/** When true, this fBm layer is inert: keeps its slot on `InterpolateConfig` but produces a plain
	 *  Catmull-Rom tessellation. UI: "Enabled" checkbox — checking deletes this field; unchecking sets it
	 *  to true. Mirrors `PathTransform.disabled`. */
	disabled?: boolean|n;

	/** Number of fBm octaves. Each octave doubles the frequency and scales amplitude by `persistence`. Default 4. */
	octaves?: number|n = 4;
	/** Per-octave amplitude multiplier (0–1). Default 0.5 (each octave half the amplitude of the previous). */
	persistence?: number|n = 0.5;
	/** When set, overrides the engine-resolved shape roughness (lateral noise amplitude as fraction of mean radius). */
	roughness?: number|n;
	/** When set, overrides the engine-resolved wavelength (world-space wavelength of the coarsest octave, in meters).
	 *  When unset, engine resolves to `meanControlPointSpacing × uniform[0.8, 1.3]`. */
	wavelength?: number|n;

	/** Width of the cross-fade band at each control point (fraction of segment length, 0–0.5): within it the noise
	 *  field blends from the previous segment's realization into this one, removing the double-bump/kink that
	 *  per-segment seeding produces at CPs. 0 = disabled (pure per-segment noise); default 0.2; only meaningful when `noiseSpace = "curve"`. */
	controlPointBlendBand?: number|n = 0.2;

	/** Coordinate space sampled in: `"curve"` = per-segment arc-position (drag-stable, but discontinuous/V at CPs; `controlPointBlendBand` mitigates);
	 *  `"local"` (default) = backbone LOCAL coords: continuous 2D field, no V/CP-clustering, stable under translation but NOT rotation;
	 *  `"world"` = WORLD coords: one global field, natural for coastlines; translation changes bumps; needs `InterpolateSeedContext.objWorldPos`. */
	noiseSpace?: NoiseSpace|n = "local";
}

export const NoiseSpace_values = ["curve", "local", "world"] as const;
export type NoiseSpace = typeof NoiseSpace_values[number];